import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface DashboardData {
  kpis: {
    total_businesses: number; active_businesses: number; total_users: number; total_queues: number;
    todays_tokens: number; monthly_revenue: number; active_subscriptions: number;
  };
  trends: {
    businesses: number; active_businesses: number; users: number; queues: number;
    tokens: number; revenue: number; subscriptions: number;
  };
  live_overview: { waiting: number; serving: number; completed: number; skipped: number; cancelled: number; not_attended: number; pre_booked: number; walk_in: number };
  subscription_status: { active: number; trial: number; expired: number; cancelled: number; total: number };
  charts: {
    months: string[]; booking_growth: number[]; revenue_growth: number[];
    top_categories: Array<{ name: string; count: number }>;
  };
  top_businesses: Array<{ name: string; bookings: number; revenue: number }>;
  recent_activities: Array<{ kind: string; title: string; subtitle: string; time: string }>;
  pending_activations: Array<{ queue: string; business: string; submitted: string; status: string }>;
  inactive: Array<{ business: string; queue: string; last_active: string; reason: string }>;
}

interface State {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches the admin dashboard composite payload from the backend.
 * `from`/`to` (YYYY-MM-DD) scope the token-activity metrics (Tokens KPI + Live Overview);
 * omit them to default to today. Refetches whenever the range changes.
 */
export function useDashboard(from?: string, to?: string) {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params: Record<string, string> = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axiosInstance.get(API_ENDPOINTS.DASHBOARD, { params });
      const data = (res.data?.data ?? res.data) as DashboardData;
      setState({ data, loading: false, error: null });
    } catch (err: any) {
      setState({ data: null, loading: false, error: err?.response?.data?.message || 'Failed to load dashboard.' });
    }
  }, [from, to]);

  useEffect(() => { void load(); }, [load]);

  return { ...state, reload: load };
}
